import { hbs } from 'ember-cli-htmlbars';
const __COLOCATED_TEMPLATE__ = hbs("{{! @glint-nocheck: not typesafe yet }}\n<li aria-label={{t \"content_pillars.pillars.content_pillars\"}} class=\"cID--pillar\" tabindex=\"0\">\n  {{#if @contentPillar.title}}\n    <Planner::ContentPillars::Card\n      @contentPillar={{@contentPillar}}\n      @displayUsageAlert={{this.displayUsageAlert}}\n      @isEditing={{this.isEditing}}\n      @remainingAICredits={{@remainingAICredits}}\n      @setEditing={{this.setEditing}}\n    />\n    <div class=\"cID--pillar__body\" role=\"group\">\n      <Planner::ContentPillars::AddContentIdeas\n        @addContentIdea={{this.addContentIdea}}\n        @suggestContentIdea={{this.suggestContentIdea}}\n      />\n      {{#if this.hasContentIdeas}}\n        <Planner::ContentPillars::ContentIdeas::List\n          @dismissInstructions={{this.dismissInstructions}}\n          @displayInstructionCard={{this.displayInstructionCard}}\n          @shouldInitializeEditor={{this.shouldInitializeEditor}}\n          @sortedContentIdeas={{this.sortedContentIdeas}}\n        />\n      {{/if}}\n    </div>\n  {{else if (or this.didReceivePlatformError this.manualFailure)}}\n    <div class=\"cID--pillar__body\">\n      <Planner::ContentPillars::Column::Error />\n    </div>\n  {{else}}\n    <div class=\"cID--pillar__body\">\n      <Planner::ContentPillars::Column::Loading />\n    </div>\n  {{/if}}\n</li>", {"contents":"{{! @glint-nocheck: not typesafe yet }}\n<li aria-label={{t \"content_pillars.pillars.content_pillars\"}} class=\"cID--pillar\" tabindex=\"0\">\n  {{#if @contentPillar.title}}\n    <Planner::ContentPillars::Card\n      @contentPillar={{@contentPillar}}\n      @displayUsageAlert={{this.displayUsageAlert}}\n      @isEditing={{this.isEditing}}\n      @remainingAICredits={{@remainingAICredits}}\n      @setEditing={{this.setEditing}}\n    />\n    <div class=\"cID--pillar__body\" role=\"group\">\n      <Planner::ContentPillars::AddContentIdeas\n        @addContentIdea={{this.addContentIdea}}\n        @suggestContentIdea={{this.suggestContentIdea}}\n      />\n      {{#if this.hasContentIdeas}}\n        <Planner::ContentPillars::ContentIdeas::List\n          @dismissInstructions={{this.dismissInstructions}}\n          @displayInstructionCard={{this.displayInstructionCard}}\n          @shouldInitializeEditor={{this.shouldInitializeEditor}}\n          @sortedContentIdeas={{this.sortedContentIdeas}}\n        />\n      {{/if}}\n    </div>\n  {{else if (or this.didReceivePlatformError this.manualFailure)}}\n    <div class=\"cID--pillar__body\">\n      <Planner::ContentPillars::Column::Error />\n    </div>\n  {{else}}\n    <div class=\"cID--pillar__body\">\n      <Planner::ContentPillars::Column::Loading />\n    </div>\n  {{/if}}\n</li>","moduleName":"later/components/planner/content-pillars/column.hbs","parseOptions":{"srcName":"later/components/planner/content-pillars/column.hbs"}});
import { action } from '@ember/object';
import { sort } from '@ember/object/computed';
import { cancel, later } from '@ember/runloop';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { task } from 'ember-concurrency';
import { tracked } from 'tracked-built-ins';

import { CONTENT_PILLAR_ONBOARDING_CACHE_KEY } from 'later/components/planner/setup-pillars';
import { SegmentEventTypes } from 'later/utils/constants/segment-events';

import type { EmberRunTimer } from '@ember/runloop/types';
import type Store from '@ember-data/store';
import type { Task } from 'ember-concurrency';
import type IntlService from 'ember-intl/services/intl';
import type ContentIdeaModel from 'later/models/content-idea';
import type ContentPillarModel from 'later/models/content-pillar';
import type AiCreditsService from 'later/services/ai-credits';
import type AlertsService from 'later/services/alerts';
import type AuthService from 'later/services/auth';
import type CacheService from 'later/services/cache';
import type ErrorsService from 'later/services/errors';
import type SegmentService from 'later/services/segment';

enum IdeaGenerationLocations {
  PillarEmptyState = 'pillar empty state',
  AdditionalIdeas = 'additional ideas'
}

interface ColumnComponentArgs {
  contentPillar: ContentPillarModel;
  fetchUsagesRemaining: Task<void, []>;
  index: number;
  remainingAICredits: number;
}

const sortContentIdeas = (a: ContentIdeaModel, b: ContentIdeaModel): number => {
  if (!a.createdAt) {
    return -1;
  }
  if (!b.createdAt) {
    return 1;
  }
  return b.createdAt - a.createdAt;
};

export default class ColumnComponent extends Component<ColumnComponentArgs> {
  @service declare aiCredits: AiCreditsService;
  @service declare alerts: AlertsService;
  @service declare auth: AuthService;
  @service declare cache: CacheService;
  @service declare errors: ErrorsService;
  @service declare intl: IntlService;
  @service declare segment: SegmentService;
  @service declare store: Store;

  contentIdeaSorting = ['createdAt:desc'];
  timers: EmberRunTimer[] = [];

  @tracked displayInstructionsDismissed = false;
  @tracked isEditing = false;
  @tracked manualFailure = false;

  userGeneratedIdeas: string[] = tracked([]);

  @sort('contentIdeas', sortContentIdeas) declare sortedContentIdeas: ContentPillarModel['contentIdeas'];

  constructor(owner: unknown, args: ColumnComponentArgs) {
    super(owner, args);
    this.#performManualReload(30000);
    this.#performManualReload(60000);
    this.#setManualFailure(61000);
  }

  willDestroy(): void {
    super.willDestroy();
    this.timers.forEach((timer: EmberRunTimer) => cancel(timer));
  }

  get contentIdeas(): ContentPillarModel['contentIdeas'] {
    return this.args.contentPillar.contentIdeas;
  }

  get didReceivePlatformError(): boolean {
    return Boolean(this.args.contentPillar.platformError && !this.args.contentPillar.title);
  }

  get displayInstructionCard(): boolean {
    return Boolean(this.args.index === 0 && this.isOnboardingPillars && !this.displayInstructionsDismissed);
  }

  get hasContentIdeas(): boolean {
    return Boolean(this.args.contentPillar?.contentIdeas?.length);
  }

  get isOnboardingPillars(): boolean {
    return Boolean(this.cache.retrieve(CONTENT_PILLAR_ONBOARDING_CACHE_KEY));
  }

  get pillarNotGenerated(): boolean {
    return !this.args.contentPillar.generatedTitle;
  }

  #performManualReload(timeout: number): void {
    const timer = later(
      this,
      () => {
        if (this.pillarNotGenerated) {
          this.args.contentPillar.reload();
        }
      },
      timeout
    );
    this.timers.push(timer);
  }

  #setManualFailure(timeout: number): void {
    const timer = later(
      this,
      () => {
        if (this.pillarNotGenerated && !this.didReceivePlatformError) {
          this.segment.track(SegmentEventTypes.AiGenerationTimeout, {
            model: 'content pillar',
            id: this.args.contentPillar.id
          });
        }

        if (this.pillarNotGenerated) {
          this.manualFailure = true;
        }
      },
      timeout
    );
    this.timers.push(timer);
  }

  @action
  dismissInstructions(): void {
    this.displayInstructionsDismissed = true;
    this.cache.remove(CONTENT_PILLAR_ONBOARDING_CACHE_KEY);
  }

  @action
  displayUsageAlert(): void {
    this.alerts.alert(this.intl.t('alerts.content_pillars.credits.limit_reached_message'), {
      title: this.intl.t('alerts.content_pillars.credits.limit_reached_title'),
      preventDuplicates: true
    });
  }

  @action
  setEditing(value: boolean): void {
    this.isEditing = value;
  }

  @action
  shouldInitializeEditor(contentIdeaId: string): boolean {
    return this.userGeneratedIdeas.includes(contentIdeaId);
  }

  addContentIdea = task(async () => {
    try {
      const contentIdea = await this.store.createRecord('content-idea', {
        contentPillar: this.args.contentPillar
      });
      this.userGeneratedIdeas.push(contentIdea.id);
      this.segment.track(SegmentEventTypes.GeneratedContentIdeas, {
        ai_generated: false,
        best_describes: this.auth.currentAccount.bestDescribes ?? '',
        location: IdeaGenerationLocations.AdditionalIdeas
      });
    } catch (error) {
      this.errors.log('addContentIdea Error', error);
    }
  });

  generateSuggestion = task(async (event: MouseEvent | KeyboardEvent) => {
    event.preventDefault();
    try {
      if (!this.aiCredits.remainingAICredits) {
        return this.displayUsageAlert();
      }
      // Note: A model instance is currently required for calling collectionActions,
      // so we instantiate and unload a temporary one.
      const temporaryContentIdea = await this.store.createRecord('content-idea');
      await temporaryContentIdea.generate({
        content_pillar_id: this.args.contentPillar.id,
        count: 3
      });
      temporaryContentIdea.unloadRecord();
      this.segment.track(SegmentEventTypes.GeneratedContentIdeas, {
        ai_generated: true,
        best_describes: this.auth.currentAccount.bestDescribes ?? '',
        location: IdeaGenerationLocations.AdditionalIdeas
      });
    } catch (error) {
      this.errors.log('suggestContentIdea Error', error);
    }
  });

  suggestContentIdea = task(async (event: MouseEvent | KeyboardEvent) => {
    this.aiCredits.performWithAiCreditSync.perform(event, this.generateSuggestion);
  });
}
